import { ArrowDropDown } from "@mui/icons-material";
import { Button, Menu, MenuItem, Stack } from "@mui/material";
import { OneTimeSeatWarningDialog } from "components/OneTimeSeatWarningDialog";
import { Account, ServiceSummary, User } from "data/interfaces";
import { useState } from "react";

interface UserActionsProps {
  selectedUsers: User[];
  account?: Account;
  primaryAdmin: boolean;
  services: ServiceSummary[];
  onRemoveUsers: (users: User[]) => void;
  onActivateServices: () => void;
  onSetAdminAccess: (users: User[], isAdmin: boolean) => void;
}

export default function UserActions({
  selectedUsers,
  account,
  primaryAdmin,
  services,
  onRemoveUsers,
  onActivateServices,
  onSetAdminAccess,
}: UserActionsProps) {
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [removeWarningOpen, setRemoveWarningOpen] = useState(false);

  const openActionsMenu = (event: any) =>
    setActionsAnchorEl(event.currentTarget);
  const closeActionsMenu = () => setActionsAnchorEl(null);

  const oneTimeSeatLicenseReferences = new Set(
    services.filter((s) => s.oneTimeSeats).map((s) => s.licenseReference),
  );

  const selectedUsersHaveOneTimeSeat = selectedUsers.some((user) =>
    user.services.some((s) =>
      oneTimeSeatLicenseReferences.has(s.licenseReference) && s.claims.length > 0,
    ),
  );

  const isPrimaryAdminSelected =
    account &&
    selectedUsers
      .filter((user) => user.id !== null)
      .map((user) => user.email.toLocaleLowerCase())
      .includes(account.admin.toLocaleLowerCase());

  const handleRemoveUsers = (event: any) => {
    closeActionsMenu();
    if (selectedUsersHaveOneTimeSeat) {
      setRemoveWarningOpen(true);
    } else {
      onRemoveUsers(selectedUsers);
    }
  };

  const handleRemoveWarningConfirm = () => {
    setRemoveWarningOpen(false);
    onRemoveUsers(selectedUsers);
  };

  const handleActivateServices = (event: any) => {
    onActivateServices();
    closeActionsMenu();
  };

  const handleGrantAdminAccess = (event: any) => {
    onSetAdminAccess(selectedUsers, true);
    closeActionsMenu();
  };

  const handleRevokeAdminAccess = (event: any) => {
    onSetAdminAccess(selectedUsers, false);
    closeActionsMenu();
  };

  return (
    <Stack spacing={2} direction="row">
      <OneTimeSeatWarningDialog
        open={removeWarningOpen}
        variant="unassign"
        onClose={() => setRemoveWarningOpen(false)}
        onConfirm={handleRemoveWarningConfirm}
      />
      <Button
        color="default"
        variant="outlined"
        disabled={!selectedUsers.length}
        endIcon={<ArrowDropDown />}
        onClick={openActionsMenu}
      >
        Actions
      </Button>
      <Menu
        anchorEl={actionsAnchorEl}
        keepMounted
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
      >
        <MenuItem onClick={handleActivateServices}>Activate services</MenuItem>
        <MenuItem
          disabled={!primaryAdmin || isPrimaryAdminSelected}
          onClick={handleGrantAdminAccess}
        >
          Grant account admin permissions
        </MenuItem>
        <MenuItem
          disabled={!primaryAdmin || isPrimaryAdminSelected}
          onClick={handleRevokeAdminAccess}
        >
          Revoke account admin permissions
        </MenuItem>
        <MenuItem disabled={isPrimaryAdminSelected} onClick={handleRemoveUsers}>
          Remove users
        </MenuItem>
      </Menu>
    </Stack>
  );
}
